import React, { useState, useEffect, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ReviewServices } from "@/services/bedrock";
import { Button, Textarea, Skeleton } from "@/components/ui";
import {
  Sparkles,
  CheckCircle,
  ChevronLeft,
  XOctagon,
  AlertTriangle,
  ExternalLink,
  Copy,
  Check,
  Inbox,
  MessageSquareCheck,
} from "lucide-react";
import { ReviewHeroCard, ReviewReplyItem, ReviewResponseRefinement } from "@/components/reviews";
import { ReviewSuccessDialog } from "../components/ReviewInbox/components";
import { URLS, useComplianceChecker, useIsMobile } from "@/utils";
import MetaTags from "@/components/MetaTags";
import { socialNetworkNames, SocialNetworksId } from "@/interfaces";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export function ReviewResponsePage() {
  const { reviewId } = useParams<{ reviewId: string }>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const [editedText, setEditedText] = useState("");
  const [showRefinement, setShowRefinement] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Fetch full review details
  const { data: review, isLoading: isLoadingReview } = useQuery({
    queryKey: ["review-details", reviewId],
    queryFn: () =>
      ReviewServices.getReview(reviewId, {
        extraFields: ["review_replies", "social_identity"],
      }),
    enabled: !!reviewId,
  });

  // Fetch AI suggested response
  const { data: generatedTextData, isLoading: isGeneratingText } = useQuery({
    queryKey: ["reviews-text-generation", reviewId],
    queryFn: () => ReviewServices.generateResponseText(Number(reviewId), {}),
    enabled: !!reviewId && !!review && (!review.reviewReplies || review.reviewReplies.length === 0),
  });
  const [isRefining, setIsRefining] = useState(false);

  const generatedText = generatedTextData?.response || "";

  // Initialize edited text when generation is complete
  useEffect(() => {
    if (generatedText && !editedText) {
      setEditedText(generatedText);
    }
  }, [generatedText]);

  // Compliance checking
  const { findBlockedWords, findFlaggedWords } = useComplianceChecker();
  const blockedWords = useMemo(() => findBlockedWords(editedText || ""), [editedText, findBlockedWords]);
  const flaggedWords = useMemo(() => findFlaggedWords(editedText || ""), [editedText, findFlaggedWords]);
  const hasBlockedWords = blockedWords.length > 0;
  const hasFlaggedWords = flaggedWords.length > 0;

  const platformName = review
    ? socialNetworkNames[review.sourceSystem as SocialNetworksId] || review.sourceSystem
    : "Platform";
  const isZillow = review?.sourceSystem === "ZL";
  const hasReplies = (review?.reviewReplies?.length || 0) > 0;

  const handlePostReply = async () => {
    if (!review || !editedText.trim()) return;
    setIsSubmitting(true);

    try {
      if (isZillow) {
        // Copy & Go flow
        navigator.clipboard.writeText(editedText);
        setCopied(true);
        toast.success("Response copied! Redirecting to Zillow...");

        await ReviewServices.updateReview(review.id, { replyStatus: "replied" });

        setTimeout(() => {
          setCopied(false);
          const publicUrl = review.socialIdentity?.publicUrl;
          if (publicUrl) window.open(publicUrl, "_blank");
          queryClient.invalidateQueries({ queryKey: ["review-details", reviewId] });
          setShowSuccess(true);
        }, 1500);
      } else {
        // Direct post flow
        await ReviewServices.submitReviewReply(review.id, { message: editedText });
        toast.success(`Reply posted successfully!`);
        queryClient.invalidateQueries({ queryKey: ["review-details", reviewId] });
        setShowSuccess(true);
      }
    } catch (error) {
      console.error("Failed to post reply:", error);
      toast.error("Failed to post reply. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (isLoadingReview) {
      return (
        <div className="space-y-6 animate-pulse">
          <Skeleton className="h-64 w-full rounded-2xl" />
          <Skeleton className="h-48 w-full rounded-2xl" />
        </div>
      );
    }

    if (!review) {
      return (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <AlertTriangle className="size-12 text-amber-500 mb-4" />
          <h2 className="text-xl font-bold text-slate-800">Review Not Found</h2>
          <p className="text-slate-500 max-w-xs mx-auto mt-2">
            The review you're looking for might have been removed or you don't have access to it.
          </p>
          <Button variant="outline" className="mt-6" onClick={() => history.push(URLS.player.reputation.inbox)}>
            Go to Review Inbox
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-4 md:space-y-8">
        <ReviewHeroCard review={review} hideReplies={true} />

        {hasReplies ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <div className="bg-emerald-100 text-emerald-700 p-1 rounded-md">
                <MessageSquareCheck className="size-4" />
              </div>
              <div className="text-lg font-bold text-slate-800">Your Response</div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <div className="divide-y divide-slate-100">
                {review.reviewReplies?.map((reply, idx) => (
                  <ReviewReplyItem key={idx} reply={reply} />
                ))}
              </div>
            </div>

            {!isMobile && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  className="gap-2 font-bold h-11 px-8 border-slate-200 hover:bg-slate-50 text-slate-600"
                  onClick={() => history.push(URLS.player.reputation.inbox)}
                >
                  <Inbox className="size-4" />
                  View All Reviews
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2">
                <div className="text-lg font-bold text-slate-800 tracking-tight leading-none">Suggested Response</div>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowRefinement(!showRefinement)}
                className="text-primary font-bold text-sm hover:bg-indigo-50 pr-0"
              >
                <Sparkles className="size-4 mr-1.5" />
                {showRefinement ? "Close AI Tools" : "Refine with AI"}
              </Button>
            </div>

            <div className="flex flex-col gap-6">
              {showRefinement && (
                <div className="bg-white border-2 border-indigo-100 p-6 rounded-2xl animate-in slide-in-from-top-2 duration-300 shadow-sm">
                  <ReviewResponseRefinement
                    reviewId={review.id}
                    mode="text"
                    currentResponse={editedText}
                    onRefined={setEditedText}
                    onRefining={setIsRefining}
                    isSubmitting={isSubmitting}
                  />
                </div>
              )}

              <div className="relative">
                <Textarea
                  value={editedText}
                  onChange={(e: any) => setEditedText(e.target.value)}
                  disabled={isRefining || isSubmitting}
                  autoSize
                  className="min-h-[100px] text-lg sm:text-base leading-relaxed p-6 bg-white border-2 border-primary/10 focus:bg-white focus:border-primary focus:ring-1 focus:ring-primary rounded-2xl shadow-sm resize-none font-medium text-slate-700 transition-all outline-none"
                  placeholder="Type your response here..."
                />

                {(isRefining || isGeneratingText) && (
                  <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] flex flex-col items-center justify-center rounded-2xl">
                    <Sparkles className="size-6 text-primary animate-pulse mb-2" />
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Generating...</span>
                  </div>
                )}
              </div>

              {/* Compliance Alerts */}
              {hasBlockedWords ? (
                <div className="flex items-start gap-2.5 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-sm font-medium leading-relaxed">
                  <XOctagon className="size-4 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-extrabold block uppercase tracking-wider text-rose-900 mb-0.5">
                      Blocked Content
                    </span>
                    Contains restricted terms:{" "}
                    <span className="font-extrabold underline">{blockedWords.join(", ")}</span>
                  </div>
                </div>
              ) : hasFlaggedWords ? (
                <div className="flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm font-medium leading-relaxed">
                  <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-extrabold block uppercase tracking-wider text-amber-900 mb-0.5">
                      Compliance Warning
                    </span>
                    Contains flagged terms: <span className="font-extrabold underline">{flaggedWords.join(", ")}</span>
                  </div>
                </div>
              ) : undefined}

              <div className="flex flex-col sm:flex-row gap-3 w-full justify-end">
                {!isMobile && (
                  <Button
                    variant="outline"
                    className={"h-12"}
                    onClick={() => history.push(URLS.player.reputation.inbox)}
                  >
                    Reply Later
                  </Button>
                )}

                <Button
                  onClick={handlePostReply}
                  className={"h-12"}
                  disabled={isSubmitting || isGeneratingText || !editedText.trim() || hasBlockedWords}
                >
                  {isZillow ? (
                    copied ? (
                      <>
                        <Check className="size-5" /> Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="size-5" /> Copy & Go to Zillow <ExternalLink className="size-4" />
                      </>
                    )
                  ) : (
                    <>
                      <CheckCircle className="size-5" /> Post Reply to {platformName}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-slate-50 min-h-screen w-screen overflow-x-hidden overflow-y-auto flex flex-col items-center">
      <MetaTags title="Respond to Review" />
      <div className="w-full max-w-3xl px-4 pt-4 md:pt-8 pb-[50vh]">
        {/* Subtle Top Nav */}
        <div className="flex items-center justify-between mb-4 md:mb-8 w-full">
          <button
            onClick={() => history.push(URLS.player.reputation.inbox)}
            className="flex items-center gap-1.5 text-slate-400 hover:text-slate-700 transition-colors border-none bg-transparent cursor-pointer font-bold text-sm"
          >
            <ChevronLeft className="size-4" />
            Review Inbox
          </button>

          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full border border-slate-200 shadow-xs select-none">
            <div className={cn("size-2 rounded-full", hasReplies ? "bg-emerald-500" : "bg-primary animate-pulse")} />
            <span className="text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">
              {hasReplies ? "Replied" : "Needs Reply"}
            </span>
          </div>
        </div>

        <div className="w-full">{renderContent()}</div>
      </div>

      <ReviewSuccessDialog
        open={showSuccess}
        onOpenChange={setShowSuccess}
        type="text"
        onNextReview={() =>
          history.push({
            pathname: URLS.player.reputation.inbox,
            search: "?needingReply=true",
          })
        }
        onDismiss={() => setShowSuccess(false)}
      />
    </div>
  );
}
